import React from "react";
import styled from "styled-components/macro";
import LabelledTextInput from "components/shared/LabelledTextInput";
import { Button } from "components/shared/standardElements";
import { COLORS, CONSTANTS, FONTSIZE } from "helpers/designConstants";
import { isValidEmail } from "helpers/bulkCheckout";

const ReviewStep = ({
  shipTo,
  isPickup,
  register,
  errors,
  getValues,
  isSubmitting,
  submissionError,
  onSubmit
}) => {
  return (
    <Page>
      <Body>
        {shipTo && (
          <Recap>
            <RecapTitle>Shipping to</RecapTitle>
            <RecapBody>
              <div>{shipTo.name}</div>
              {shipTo.company && <div>{shipTo.company}</div>}
              <div>{shipTo.streetAddress}</div>
              {shipTo.address2 && <div>{shipTo.address2}</div>}
              <div>
                {[[shipTo.city, shipTo.state].filter(Boolean).join(", "), shipTo.zip]
                  .filter(Boolean)
                  .join(" ")}
              </div>
              {shipTo.phone && <div>{shipTo.phone}</div>}
            </RecapBody>
          </Recap>
        )}

        {isPickup && <Reassurance>You'll pick this order up in person.</Reassurance>}

        <Details>
          <Message>
            <p>Please enter a PO# and your email address.</p>
            <p>
              A copy of your order will be sent to your adidas rep as well as the
              email below.
            </p>
          </Message>
          <FieldGroup>
            <LabelledTextInput
              labelText="Purchase Order #"
              error={errors.po}
              {...register("po", { required: "PO is required" })}
            />
            <ErrorMessage>{errors.po?.message}</ErrorMessage>
          </FieldGroup>
          <FieldGroup>
            <LabelledTextInput
              labelText="Enter your name"
              error={errors.name}
              {...register("name", { required: "Your name is required" })}
            />
            <ErrorMessage>{errors.name?.message}</ErrorMessage>
          </FieldGroup>
          <FieldGroup>
            <LabelledTextInput
              labelText="Enter your email"
              error={errors.email1}
              {...register("email1", {
                onChange: (e) => (e.target.value = e.target.value.trim()),
                validate: (v) => isValidEmail(v) || "Invalid email address"
              })}
            />
            <ErrorMessage>{errors.email1?.message}</ErrorMessage>
          </FieldGroup>
          <FieldGroup>
            <LabelledTextInput
              labelText="Confirm your email"
              error={errors.email2}
              {...register("email2", {
                onChange: (e) => (e.target.value = e.target.value.trim()),
                validate: (v) => v === getValues("email1") || "Emails must match"
              })}
            />
            <ErrorMessage>{errors.email2?.message}</ErrorMessage>
          </FieldGroup>
        </Details>
      </Body>

      <FooterBar>
        <SubmissionInfo isError={submissionError != null}>
          {isSubmitting
            ? "Please wait while your order is submitted. This may take a moment, do not close this page."
            : submissionError
            ? "There was an error submitting your order. Please wait a few minutes and try again. If the problem persists, please contact your adidas rep."
            : " "}
        </SubmissionInfo>
        <SubmitButton type="button" disabled={isSubmitting} onClick={onSubmit}>
          Submit
        </SubmitButton>
      </FooterBar>
    </Page>
  );
};

const Page = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: 1fr min-content;
`;
const Body = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: left;
  overflow-y: auto;
  padding: 20px;
`;
const Recap = styled.div`
  border: 1px solid ${COLORS.secondary};
  border-radius: 6px;
  padding: 10px;
`;
const RecapTitle = styled.div`
  text-transform: uppercase;
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.text};
`;
const RecapBody = styled.div`
  color: ${COLORS.text};
  font-size: ${FONTSIZE.small};
  padding-top: 6px;
`;
const Reassurance = styled.div`
  color: ${COLORS.text};
  font-size: ${FONTSIZE.small};
`;
const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const Message = styled.div`
  color: ${COLORS.text};
`;
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;
const ErrorMessage = styled.div`
  height: 15px;
  color: ${COLORS.alertRed};
  font-size: ${FONTSIZE.xsmall};
  padding-top: 0px;
  padding-right: 5px;
`;
const FooterBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 15px 0 30px;
  background: ${COLORS.tertiary};
  border-radius: 0 0 ${CONSTANTS.borderRadius} ${CONSTANTS.borderRadius};
`;
const SubmissionInfo = styled.div`
  color: ${({ isError }) => (isError ? COLORS.alertRed : COLORS.alertOrange)};
  font-size: ${FONTSIZE.small};
  text-align: center;
  min-height: 18px;
  padding: 0 20px;
`;
const SubmitButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export default ReviewStep;
